'use client';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WatchOutlinedIcon from '@mui/icons-material/WatchOutlined';
import ContrastOutlinedIcon from '@mui/icons-material/ContrastOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import CallReceivedOutlinedIcon from '@mui/icons-material/CallReceivedOutlined';
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { SETTINGS_SECTIONS, hasAnyPermission, settingsSectionHref } from '@/app/settings/sections';

export interface NavigationSubItem {
  name: string;
  href: string;
  icon: React.ElementType;
  current?: boolean;
}

export interface NavigationItem {
  name: string;
  href?: string;
  onClick?: () => void;
  icon: React.ElementType;
  current?: boolean;
  subItems?: NavigationSubItem[];
}

export interface PageDestination {
  name: string;
  href: string;
  group?: string;
  keywords?: string;
}

const allows = (permissions: string[], permission: string) => permissions.includes(permission);

export function buildNavigationItems({ permissions, pathname, onLogout }: { permissions: string[]; pathname: string; onLogout: () => void }): NavigationItem[] {
  const canViewIncomingOrders = allows(permissions, 'VIEW_INCOMING_ORDERS');
  const canViewOutgoingOrders = allows(permissions, 'VIEW_OUTGOING_ORDERS');
  const canEditOutgoingOrders = allows(permissions, 'EDIT_OUTGOING_ORDERS');
  const canViewFinancials = allows(permissions, 'VIEW_FINANCIAL_DATA');
  const canViewBankTransactions = canViewFinancials && allows(permissions, 'VIEW_BANKING');
  const canViewDocusign = allows(permissions, 'VIEW_DOCUSIGN');
  const canViewInventory = allows(permissions, 'VIEW_INVENTORY');
  const canViewLeads = allows(permissions, 'VIEW_LEADS');
  const canViewInvoices = allows(permissions, 'VIEW_PURCHASE_INVOICE');
  const canViewSalesInvoice = allows(permissions, 'VIEW_SALES_INVOICE');
  const canViewUsers = allows(permissions, 'VIEW_USERS');
  const canViewAuditLogs = allows(permissions, 'VIEW_AUDIT_LOGS');
  const canViewAppSettings = permissions.some((permission) => permission.startsWith('VIEW_SETTINGS_')) || allows(permissions, 'VIEW_BANKING');
  const canViewPolish = allows(permissions, 'VIEW_POLISH');
  const canViewJobs = allows(permissions, 'VIEW_JOBS');
  const canViewDraftPostings = allows(permissions, 'VIEW_DRAFT_POSTINGS');
  const canViewInspectionReports = allows(permissions, 'VIEW_INSPECTION_REPORTS');
  const canViewCustomers = allows(permissions, 'VIEW_CUSTOMERS');
  const canViewDealerCatalog = allows(permissions, 'VIEW_DEALER_CATALOG');
  const canViewTodos = allows(permissions, 'VIEW_TASKS') || allows(permissions, 'VIEW_ALL_TASKS');
  return [
    ...(canViewInventory || canViewLeads
      ? [
          {
            name: 'Inventory',
            href: canViewInventory ? '/inventory' : '/inventory/leads',
            icon: InventoryIcon,
            current: pathname.startsWith('/inventory'),
            subItems: [
              ...(canViewInventory
                ? [
                    {
                      name: 'Watches',
                      href: '/inventory',
                      icon: WatchOutlinedIcon,
                      current: pathname === '/inventory',
                    },
                    {
                      name: 'Dials',
                      href: '/inventory/dials',
                      icon: ContrastOutlinedIcon,
                      current: pathname === '/inventory/dials',
                    },
                    {
                      name: 'Bracelets',
                      href: '/inventory/bracelets',
                      icon: LinkOutlinedIcon,
                      current: pathname === '/inventory/bracelets',
                    },
                    {
                      name: 'Alerts',
                      href: '/inventory/alerts',
                      icon: NotificationsNoneOutlinedIcon,
                      current: pathname === '/inventory/alerts',
                    },
                  ]
                : []),
              ...(canViewLeads
                ? [
                    {
                      name: 'Leads',
                      href: '/inventory/leads',
                      icon: PersonSearchOutlinedIcon,
                      current: pathname === '/inventory/leads',
                    },
                  ]
                : []),
            ],
          },
        ]
      : canViewDealerCatalog
        ? [
            {
              name: 'Inventory',
              href: '/dealer',
              icon: InventoryIcon,
              current: pathname.startsWith('/dealer'),
            },
          ]
        : []),
    ...(canViewIncomingOrders || canViewOutgoingOrders
      ? [
          {
            name: 'Orders',
            icon: ShoppingCartIcon,
            current: pathname.startsWith('/orders') || pathname.startsWith('/auctions'),
            subItems: [
              ...(canViewIncomingOrders
                ? [
                    {
                      name: 'Incoming',
                      href: '/orders/incoming',
                      icon: CallReceivedOutlinedIcon,
                      current: pathname === '/orders/incoming',
                    },
                  ]
                : []),
              ...(canViewOutgoingOrders
                ? [
                    {
                      name: 'Outgoing',
                      href: '/orders',
                      icon: CallMadeOutlinedIcon,
                      current: pathname === '/orders',
                    },
                    ...(canEditOutgoingOrders
                      ? [
                          {
                            name: 'Approvals',
                            href: '/orders/approvals',
                            icon: AssignmentTurnedInOutlinedIcon,
                            current: pathname === '/orders/approvals',
                          },
                        ]
                      : []),
                    {
                      name: 'Auctions',
                      href: '/auctions',
                      icon: GavelOutlinedIcon,
                      current: pathname === '/auctions',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewFinancials
      ? [
          {
            name: 'Analytics',
            icon: BarChartIcon,
            current: pathname.startsWith('/analytics'),
            subItems: [
              {
                name: 'Inventory',
                href: '/analytics/summary',
                icon: Inventory2OutlinedIcon,
                current: pathname === '/analytics/summary',
              },
              {
                name: 'P&L',
                href: '/analytics/pl',
                icon: ShowChartOutlinedIcon,
                current: pathname === '/analytics/pl',
              },
              {
                name: 'Inspections',
                href: '/analytics/inspections',
                icon: FactCheckOutlinedIcon,
                current: pathname === '/analytics/inspections',
              },
              {
                name: 'Image Updates',
                href: '/analytics/picture-updates',
                icon: PhotoCameraOutlinedIcon,
                current: pathname === '/analytics/picture-updates',
              },
              {
                name: 'Marketplace Postings',
                href: '/analytics/postings',
                icon: StorefrontOutlinedIcon,
                current: pathname === '/analytics/postings',
              },
            ],
          },
        ]
      : []),
    ...(canViewInvoices || canViewSalesInvoice || canViewFinancials || canViewDocusign || canViewCustomers
      ? [
          {
            name: 'Accounting',
            icon: AccountBalanceIcon,
            current: pathname.startsWith('/invoices') || pathname.startsWith('/docusign') || pathname === '/banking',
            subItems: [
              ...(canViewInvoices
                ? [
                    {
                      name: 'Purchase Invoices',
                      href: '/invoices',
                      icon: ReceiptLongOutlinedIcon,
                      current: pathname === '/invoices',
                    },
                  ]
                : []),
              ...(canViewSalesInvoice
                ? [
                    {
                      name: 'Proforma Invoices',
                      href: '/invoices/proforma',
                      icon: RequestQuoteOutlinedIcon,
                      current: pathname === '/invoices/proforma',
                    },
                    {
                      name: 'Sales Invoices',
                      href: '/invoices/sales',
                      icon: ReceiptOutlinedIcon,
                      current: pathname === '/invoices/sales',
                    },
                  ]
                : []),
              ...(canViewDocusign
                ? [
                    {
                      name: 'Purchase Agreements',
                      href: '/docusign',
                      icon: HandshakeOutlinedIcon,
                      current: pathname === '/docusign',
                    },
                  ]
                : []),
              ...(canViewBankTransactions
                ? [
                    {
                      name: 'Bank Transactions',
                      href: '/banking',
                      icon: AccountBalanceWalletOutlinedIcon,
                      current: pathname === '/banking',
                    },
                  ]
                : []),
              ...(canViewCustomers
                ? [
                    {
                      name: 'Customers',
                      href: '/invoices/buyers',
                      icon: PeopleOutlineIcon,
                      current: pathname === '/invoices/buyers',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewInspectionReports || canViewInventory || canViewPolish || canViewAuditLogs || canViewJobs || canViewDraftPostings || canViewTodos
      ? [
          {
            name: 'Operations',
            icon: AccountTreeIcon,
            current:
              pathname.startsWith('/inspection-reports') ||
              pathname === '/labels' ||
              pathname === '/polish' ||
              pathname === '/audit' ||
              pathname === '/jobs' ||
              pathname.startsWith('/draft-postings') ||
              pathname === '/todos',
            subItems: [
              ...(canViewInventory
                ? [
                    {
                      name: 'Shipping Labels',
                      href: '/labels',
                      icon: LocalShippingOutlinedIcon,
                      current: pathname === '/labels',
                    },
                  ]
                : []),
              ...(canViewPolish
                ? [
                    {
                      name: 'Polish Queue',
                      href: '/polish',
                      icon: AutoFixHighOutlinedIcon,
                      current: pathname === '/polish',
                    },
                  ]
                : []),
              ...(canViewAuditLogs
                ? [
                    {
                      name: 'Audit Log',
                      href: '/audit',
                      icon: HistoryOutlinedIcon,
                      current: pathname === '/audit',
                    },
                  ]
                : []),
              ...(canViewDraftPostings
                ? [
                    {
                      name: 'Posting Queue',
                      href: '/draft-postings',
                      icon: DynamicFeedOutlinedIcon,
                      current: pathname.startsWith('/draft-postings'),
                    },
                  ]
                : []),
              ...(canViewJobs
                ? [
                    {
                      name: 'Data Pipelines',
                      href: '/jobs',
                      icon: HubOutlinedIcon,
                      current: pathname === '/jobs',
                    },
                  ]
                : []),
              ...(canViewInspectionReports
                ? [
                    {
                      name: 'Inspection Reports',
                      href: '/inspection-reports',
                      icon: AssignmentOutlinedIcon,
                      current: pathname.startsWith('/inspection-reports'),
                    },
                  ]
                : []),
              ...(canViewTodos
                ? [
                    {
                      name: 'Todos',
                      href: '/todos',
                      icon: ViewKanbanOutlinedIcon,
                      current: pathname === '/todos',
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(canViewUsers
      ? [
          {
            name: 'Users',
            href: '/users',
            icon: GroupIcon,
            current: pathname === '/users',
          },
        ]
      : []),
    ...(canViewAppSettings
      ? [
          {
            name: 'Settings',
            href: '/settings',
            icon: SettingsIcon,
            current: pathname === '/settings',
          },
        ]
      : []),
    {
      name: 'Sign out',
      onClick: onLogout,
      icon: LogoutIcon,
    },
  ];
}

const settingsDestinations = (permissions: string[]): PageDestination[] => {
  const groupOrder = [...new Set(SETTINGS_SECTIONS.map((section) => section.group))];
  return SETTINGS_SECTIONS.filter((section) => hasAnyPermission(section.permission, permissions))
    .sort((left, right) => groupOrder.indexOf(left.group) - groupOrder.indexOf(right.group) || left.label.localeCompare(right.label))
    .map((section) => ({
      name: section.label,
      href: settingsSectionHref(section.id),
      group: 'Settings',
      keywords: section.group,
    }));
};

/** Where "/" sends a user: the first page their role works from. */
export function homePath(permissions: string[]) {
  if (permissions.includes('APPROVE_POLISH')) return '/polish';
  if (permissions.includes('VIEW_INVENTORY')) return '/inventory';
  if (permissions.includes('VIEW_LEADS')) return '/inventory/leads';
  if (permissions.includes('VIEW_DEALER_CATALOG')) return '/dealer';
  return '/inventory';
}

export function buildPageDestinations(permissions: string[]): PageDestination[] {
  const items = buildNavigationItems({ permissions, pathname: '', onLogout: () => {} });

  return items
    .flatMap((item) => {
      if (item.subItems?.length) {
        return item.subItems.map((subItem) => ({
          name: subItem.name,
          href: subItem.href,
          group: item.name,
        }));
      }

      if (!item.href) return [];

      const destination = { name: item.name, href: item.href };
      if (item.href !== '/settings') return [destination];

      return [destination, ...settingsDestinations(permissions)];
    })
    .concat([
      ...(allows(permissions, 'VIEW_INBOX') ? [{ name: 'Inbox', href: '/inbox' }] : []),
      ...(allows(permissions, 'VIEW_PROFILE') ? [{ name: 'Profile', href: '/profile' }] : []),
      ...(allows(permissions, 'USE_TIMMY') ? [{ name: 'Timmy', href: '/timmy' }] : []),
    ]);
}
